import { isSharepointExcelWorkbookTabUrl } from 'constants/SharepointTrustedWorkbookUrls';
import { postSharepointWriteNumber } from 'lib/api/postSharepointWriteNumber';
import { getCurrentTab } from 'lib/chromeScripting/util';
import captureException from 'utils/captureException';

export type SharepointWriteFailureStage =
  | 'chrome_runtime_unavailable'
  | 'workbook_tab_url_missing'
  | 'writeback_metadata_missing'
  | 'api_request_failed';

export type SharepointWriteStatus =
  | { phase: 'pending'; targetLabel: string }
  | { phase: 'success'; targetLabel: string }
  | {
      phase: 'error';
      stage: SharepointWriteFailureStage;
      targetLabel: string;
    };

export type WriteSharepointLoadIdToWorkbookParams = {
  loadId: string;
  templateId: string;
  gridRowIndices: number[];
  worksheetName: string;
};

export type WriteSharepointLoadIdToWorkbookResult =
  { ok: true } | { ok: false; stage: SharepointWriteFailureStage };

const failureMessage: Record<SharepointWriteFailureStage, string> = {
  chrome_runtime_unavailable:
    'SharePoint writeback: Chrome extension runtime unavailable',
  workbook_tab_url_missing:
    'SharePoint writeback: active tab is not a trusted workbook URL',
  writeback_metadata_missing:
    'SharePoint writeback: suggestion metadata is incomplete',
  api_request_failed:
    'SharePoint writeback: POST /sharepoint/workbooks/load-id-writeback failed',
};

function reportWriteFailure(
  stage: SharepointWriteFailureStage,
  cause?: unknown
): void {
  const message = failureMessage[stage];
  const error =
    cause instanceof Error ? new Error(message, { cause }) : new Error(message);
  void captureException(error, {
    sharepointWriteFailureStage: stage,
  });
}

export async function writeSharepointLoadIdToWorkbook(
  params: WriteSharepointLoadIdToWorkbookParams
): Promise<WriteSharepointLoadIdToWorkbookResult> {
  const tab = await getCurrentTab({ preferSidepanelHostTab: true });
  const url = tab?.url?.trim() ?? '';
  if (!url || !isSharepointExcelWorkbookTabUrl(url)) {
    const stage =
      typeof chrome === 'undefined' || !chrome.runtime?.sendMessage
        ? 'chrome_runtime_unavailable'
        : 'workbook_tab_url_missing';
    reportWriteFailure(stage);
    return { ok: false, stage };
  }

  if (
    !params.templateId.trim() ||
    !params.worksheetName.trim() ||
    params.gridRowIndices.length === 0
  ) {
    reportWriteFailure('writeback_metadata_missing');
    return { ok: false, stage: 'writeback_metadata_missing' };
  }

  try {
    await postSharepointWriteNumber({
      templateId: params.templateId,
      url,
      worksheet: params.worksheetName,
      gridRowIndices: params.gridRowIndices,
      numberValue: params.loadId,
    });
  } catch (error) {
    reportWriteFailure('api_request_failed', error);
    return { ok: false, stage: 'api_request_failed' };
  }

  return { ok: true };
}
